import { Box, Table, Tag, Text } from "@chakra-ui/react";
import { useSelector } from "react-redux";

import TicketsPerServiceLineGraph from "../charts/TicketsPerServiceLineGraph";
import TicketsPerServicePieGraph from "../charts/TicketsPerServicePieGraph";
import { formatNumber } from "../../../store/helpers";
import {
  TICKETS_PER_SERVICE_COLUMN_DEFINITIONS,
  normalizeRenderConfig,
  sortReportRows,
} from "../../../store/renderConfig";
import * as defaultSelectors from "../../../store/selectors/defaultSelector";

const getTicketsPerServiceRows = ({ data = [], branchName = "", scope = "" }) =>
  (data || [])
    .filter((service) => Number(service?.total))
    .map((service) => ({
      ...service,
      serviceName: service.name || "",
      branchName,
      scope,
      average: service.averageTicketsPerDay,
      min: service.minTicketsPerDay,
      max: service.maxTicketsPerDay,
    }));

const getVisibleColumns = (config) =>
  TICKETS_PER_SERVICE_COLUMN_DEFINITIONS.filter((column) => config.columns.includes(column.id));

const MetricCell = ({ value, pct, color = "inherit" }) => (
  <Text color={color}>
    {formatNumber(value)}
    {pct === undefined ? null : (
      <Box className="agentTicketMutedCell">({Number(pct || 0).toFixed(2)}%)</Box>
    )}
  </Text>
);

const renderCell = (serviceData, columnId) => {
  if (columnId === "serviceName") return serviceData.serviceName || serviceData.name;
  if (columnId === "total") return <Text color="var(--blue)">{formatNumber(serviceData.total)}</Text>;
  if (columnId === "average") return formatNumber(serviceData.average);
  if (columnId === "min") return formatNumber(serviceData.min);
  if (columnId === "max") return formatNumber(serviceData.max);
  if (columnId === "completed") return <MetricCell value={serviceData.completed} pct={serviceData.pctCompleted} color="var(--green)" />;
  if (columnId === "noShow") return <MetricCell value={serviceData.noShow} pct={serviceData.pctNoShow} color="var(--red)" />;
  if (columnId === "unattended") return <MetricCell value={serviceData.unattended} pct={serviceData.pctUnattended} color="var(--red)" />;
  return null;
};

const TicketsPerServiceTable = ({
  branchId = null,
  data,
  reportIndex,
  branchIndex,
  branchName,
  numServices,
}) => {
  const renderConfig = useSelector(defaultSelectors.selectRenderConfig);
  const { showEmptyDataSets } = useSelector((state) => state.defaultReducer);
  const config = normalizeRenderConfig(renderConfig, {
    columnDefinitions: TICKETS_PER_SERVICE_COLUMN_DEFINITIONS,
  });
  const chartTitle = branchId ? `${branchName} Branch` : "Entire Organization,";
  const branchTitle = branchId
    ? `${reportIndex}. ${branchIndex ? `${branchIndex}. ` : ""}${branchName} Branch`
    : `${reportIndex}. 1. Entire Organization`;
  const rows = sortReportRows(
    getTicketsPerServiceRows({
      data,
      branchName: branchName || "",
      scope: branchId ? "Branch" : "Organization",
    }),
    config,
    { columnDefinitions: TICKETS_PER_SERVICE_COLUMN_DEFINITIONS }
  );
  const visibleColumns = getVisibleColumns(config);
  const tableClassName = `stripedTable reportDataTable reportDataTable--${config.density}`;
  const visibleChartRows = (data || []).filter(
    (item) => showEmptyDataSets || (!showEmptyDataSets && Number(item.total) > 0)
  );

  return (
    <Box mt={10}>
      <Box mb="20px">
        <h2>{branchTitle}</h2>
        {branchId ? <p>{numServices} services.</p> : null}
      </Box>

      {!config.charts.enabled ? (
        <Box className="reportTableContainer ticketsPerServiceBlock">
          <Table.Root className={tableClassName} size="sm">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>#</Table.ColumnHeader>
                {visibleColumns.map((column) => (
                  <Table.ColumnHeader key={column.id}>{column.label}</Table.ColumnHeader>
                ))}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {rows.map((serviceData, index) => (
                <Table.Row key={`${serviceData.serviceName}-${index}`}>
                  <Table.Cell>{index + 1}</Table.Cell>
                  {visibleColumns.map((column) => (
                    <Table.Cell key={column.id}>{renderCell(serviceData, column.id)}</Table.Cell>
                  ))}
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Box>
      ) : null}

      {config.charts.enabled
        ? visibleChartRows.map((serviceData, index) => (
            <Box key={index} className="reportContainer ticketsPerServiceBlock" mt="20px">
              <Box mb="10px">
                <h3>
                  {reportIndex}. {branchIndex ? `${branchIndex}. ` : null}
                  {index + 1}. {serviceData.name}
                </h3>
                {branchId ? (
                  <Tag.Root>
                    <Tag.Label className="branchTag">{branchName} Branch</Tag.Label>
                  </Tag.Root>
                ) : null}
              </Box>
              <Table.Root className="stripedTable" size="sm">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader borderRight="1px solid #dadce0">Total Tickets</Table.ColumnHeader>
                    <Table.ColumnHeader>Avg /day.</Table.ColumnHeader>
                    <Table.ColumnHeader>Min.</Table.ColumnHeader>
                    <Table.ColumnHeader borderRight="1px solid #dadce0">Max</Table.ColumnHeader>
                    <Table.ColumnHeader>Completed</Table.ColumnHeader>
                    <Table.ColumnHeader>No Show</Table.ColumnHeader>
                    <Table.ColumnHeader>Unattended</Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  <Table.Row>
                    <Table.Cell borderRight="1px solid #dadce0">{renderCell(serviceData, "total")}</Table.Cell>
                    <Table.Cell>{formatNumber(serviceData.averageTicketsPerDay)}</Table.Cell>
                    <Table.Cell>{formatNumber(serviceData.minTicketsPerDay)}</Table.Cell>
                    <Table.Cell borderRight="1px solid #dadce0">{formatNumber(serviceData.maxTicketsPerDay)}</Table.Cell>
                    <Table.Cell>{renderCell(serviceData, "completed")}</Table.Cell>
                    <Table.Cell>{renderCell(serviceData, "noShow")}</Table.Cell>
                    <Table.Cell>{renderCell(serviceData, "unattended")}</Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table.Root>
              <Box className="ticketServiceChartGrid">
                <Box className="ticketServiceChartPane ticketServiceChartPane--pie">
                  <TicketsPerServicePieGraph
                    title="Ticket Management Comparison"
                    chart={serviceData}
                    chartHeight="200px"
                  />
                </Box>
                <Box className="ticketServiceChartPane ticketServiceChartPane--line">
                  <TicketsPerServiceLineGraph
                    title={`${serviceData.name}: ${chartTitle} Day by Day Comparison`}
                    chart={serviceData.days}
                    chartHeight="200px"
                  />
                </Box>
              </Box>
            </Box>
          ))
        : null}
      {!rows.length && !config.charts.enabled ? (
        <Text fontSize="sm">No data available for this report</Text>
      ) : null}
    </Box>
  );
};

export default TicketsPerServiceTable;
